import { useState, useEffect, useCallback } from 'react';
import { Filter, X, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import SearchInput from '../shared/SearchInput';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

export default function RegistrationFilters({ filters, onChange }) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [assemblies, setAssemblies] = useState([]);

  // Fetch assemblies once
  useEffect(() => {
    api.get('/assemblies').then(res => setAssemblies(res.data)).catch(() => {});
  }, []);

  // Fetch filter options — cascading based on assemblyId and mandal
  const fetchFilters = useCallback(() => {
    const params = {};
    if (filters.assemblyId) params.assemblyId = filters.assemblyId;
    if (filters.mandal) params.mandal = filters.mandal;
    api.get('/registrations/filters', { params }).then(res => setFilterOptions(res.data)).catch(() => {});
  }, [filters.assemblyId, filters.mandal]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  const set = (key, value) => {
    const updated = { ...filters, [key]: value, page: 1 };
    if (key === 'assemblyId') {
      updated.mandal = '';
      updated.panchayat = '';
    }
    if (key === 'mandal') {
      updated.panchayat = '';
    }
    onChange(updated);
  };

  const clearAll = () => {
    onChange({ search: '', status: '', assemblyId: '', committeeType: '', level: '', mandal: '', panchayat: '', casteCategory: '', gender: '', wing: '', withoutPhoto: '', fromDate: '', toDate: '', page: 1, limit: filters.limit, sortBy: filters.sortBy, sortOrder: filters.sortOrder });
  };

  const activeCount = ['status', 'assemblyId', 'committeeType', 'level', 'mandal', 'panchayat', 'casteCategory', 'gender', 'wing', 'withoutPhoto', 'fromDate', 'toDate']
    .filter(k => filters[k]).length;

  const Select = ({ label, value, options, onChange: onCh, disabled }) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1.5">{label}</label>
      <select
        value={value || ''}
        onChange={e => onCh(e.target.value)}
        disabled={disabled}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-ysrcp-500 focus:border-ysrcp-500 disabled:bg-gray-50 disabled:text-gray-400 transition-shadow"
      >
        <option value="">All</option>
        {options.map(o => typeof o === 'object'
          ? <option key={o.value} value={o.value}>{o.label}</option>
          : <option key={o} value={o}>{o}</option>
        )}
      </select>
    </div>
  );

  return (
    <div className={`bg-white rounded-xl shadow-panel border transition-colors duration-200 p-4 ${expanded ? 'border-ysrcp-200' : 'border-gray-100'}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <SearchInput value={filters.search || ''} onChange={v => set('search', v)} placeholder="Search name, phone, voter ID, user ID..." />
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg min-h-[44px] font-medium transition-all duration-200 ${
            expanded || activeCount > 0
              ? 'bg-ysrcp-500 text-white shadow-sm hover:bg-ysrcp-600'
              : 'border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
          }`}
        >
          <Filter className="w-4 h-4" />
          <span className="hidden sm:inline">Filters</span>
          {activeCount > 0 && (
            <span className={`ml-1 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold ${
              expanded ? 'bg-white text-ysrcp-600' : 'bg-ysrcp-100 text-ysrcp-700'
            }`}>{activeCount}</span>
          )}
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {activeCount > 0 && (
          <button onClick={clearAll} className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1 font-medium">
            <X className="w-3.5 h-3.5" /> Clear all
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Select label="Status" value={filters.status} options={['SUBMITTED', 'APPROVED', 'REJECTED', 'PENDING']} onChange={v => set('status', v)} />
            {user?.role === 'SUPER_ADMIN' && (
              <Select
                label="Assembly Constituency"
                value={filters.assemblyId}
                options={assemblies.map(a => ({ value: a.id, label: `${a.name} (${a.code})` }))}
                onChange={v => set('assemblyId', v)}
              />
            )}
            <Select
              label="Mandal"
              value={filters.mandal}
              options={filterOptions.mandals || []}
              onChange={v => set('mandal', v)}
            />
            <Select
              label="Village / Panchayat"
              value={filters.panchayat}
              options={filterOptions.panchayats || []}
              onChange={v => set('panchayat', v)}
              disabled={!filters.mandal}
            />
            <Select label="Committee Type" value={filters.committeeType} options={filterOptions.committeeTypes || []} onChange={v => set('committeeType', v)} />
            <Select label="Level" value={filters.level} options={filterOptions.levels || []} onChange={v => set('level', v)} />
            <Select label="Affiliated Wing" value={filters.wing} options={filterOptions.affiliatedWings || []} onChange={v => set('wing', v)} />
            <Select label="Caste Category" value={filters.casteCategory} options={filterOptions.casteCategories || []} onChange={v => set('casteCategory', v)} />
            <Select label="Gender" value={filters.gender} options={filterOptions.genders || []} onChange={v => set('gender', v)} />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">From Date</label>
              <input type="date" value={filters.fromDate || ''} onChange={e => set('fromDate', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ysrcp-500 focus:border-ysrcp-500 transition-shadow" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">To Date</label>
              <input type="date" value={filters.toDate || ''} onChange={e => set('toDate', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ysrcp-500 focus:border-ysrcp-500 transition-shadow" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 pb-2">
                <input type="checkbox" checked={filters.withoutPhoto === 'true'} onChange={e => set('withoutPhoto', e.target.checked ? 'true' : '')} className="rounded border-gray-300 text-ysrcp-500 focus:ring-ysrcp-500" />
                Without Photo
              </label>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <Calendar className="w-4 h-4 text-gray-400 mb-1.5 flex-shrink-0" />
              <button onClick={() => { const today = new Date().toISOString().slice(0,10); onChange({ ...filters, fromDate: today, toDate: today, page: 1 }); }} className="text-xs px-3 py-1.5 rounded-md font-medium text-ysrcp-600 bg-ysrcp-50 hover:bg-ysrcp-100 transition-colors">Today</button>
              <button onClick={() => { const d = new Date(); d.setDate(d.getDate()-7); onChange({ ...filters, fromDate: d.toISOString().slice(0,10), page: 1 }); }} className="text-xs px-3 py-1.5 rounded-md font-medium text-ysrcp-600 bg-ysrcp-50 hover:bg-ysrcp-100 transition-colors">1 Week</button>
              <button onClick={() => { const d = new Date(); d.setMonth(d.getMonth()-1); onChange({ ...filters, fromDate: d.toISOString().slice(0,10), page: 1 }); }} className="text-xs px-3 py-1.5 rounded-md font-medium text-ysrcp-600 bg-ysrcp-50 hover:bg-ysrcp-100 transition-colors">1 Month</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
